/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { tokens } from '@neo4j-ndl/base';
import { color, type RgbColor, rgbToHex, validHex } from '@uiw/react-color';
import tinycolor from 'tinycolor2';

import {
  clampNumericValue,
  compareNumericValues,
  getNumericWeight,
  isBigintLiteral,
  isDatetimeLiteral,
  isLocalDatetimeLiteral,
  isTimeLiteral,
  lerpColor,
  type NumericValue,
  numericValuesEqual,
  parseDateString,
  parseLocalDateString,
  parseTimeString,
  resolveNumericLiteral,
  resolveRangeLiteral,
} from '../../_generated/style-rules';
import {
  type DataGridCellStyle,
  type DataGridColorRange,
  type DataGridStyleRule,
  type DataGridStyleRuleOperand,
  type DataGridStyleRuleValueConverter,
  type DataGridStyleRuleValueConverters,
  type DataGridStyleRuleWhere,
} from './types';

function getInterpolatedColor(
  colorRange: DataGridColorRange,
  rowData: Record<string, unknown>,
  converters?: DataGridStyleRuleValueConverters,
): string | null {
  const {
    onColumn,
    minValue,
    minColor,
    maxValue,
    maxColor,
    midValue,
    midColor,
  } = colorRange;

  if (!validHex(minColor) || !validHex(maxColor)) {
    return null;
  }

  const minRgb = color(minColor).rgb;
  const maxRgb = color(maxColor).rgb;
  const resolvedMinValue = resolveRangeLiteral(minValue);
  const resolvedMaxValue = resolveRangeLiteral(maxValue);
  if (resolvedMinValue === null || resolvedMaxValue === null) {
    return null;
  }
  const boundsOrder = compareNumericValues(resolvedMinValue, resolvedMaxValue);
  if (boundsOrder === null) {
    return null;
  }
  const actualMinValue = boundsOrder <= 0 ? resolvedMinValue : resolvedMaxValue;
  const actualMaxValue = boundsOrder <= 0 ? resolvedMaxValue : resolvedMinValue;

  let midRgb: RgbColor | undefined = undefined;
  const resolvedMidValue =
    midValue === undefined ? undefined : resolveRangeLiteral(midValue);
  if (resolvedMidValue === null) {
    return null;
  }
  if (midValue !== undefined && midColor !== undefined) {
    const minToMid = compareNumericValues(actualMinValue, resolvedMidValue!);
    const midToMax = compareNumericValues(resolvedMidValue!, actualMaxValue);
    const isMidInRange =
      minToMid !== null && midToMax !== null && minToMid <= 0 && midToMax <= 0;
    if (!isMidInRange || !validHex(midColor)) {
      return null;
    }
    midRgb = color(midColor).rgb;
  }

  const rawValue = rowData[onColumn];
  const resolved = resolveCellValue(rawValue, converters?.[onColumn]);
  let numericValue: NumericValue | null = null;
  if (typeof resolved === 'bigint') {
    numericValue = resolved;
  } else if (typeof resolved === 'number' && Number.isFinite(resolved)) {
    numericValue = resolved;
  }
  if (numericValue === null) {
    return null;
  }

  const clampedValue = clampNumericValue(
    numericValue,
    actualMinValue,
    actualMaxValue,
  );
  if (clampedValue === null) {
    return null;
  }

  let rgb: RgbColor;

  if (midRgb !== undefined && resolvedMidValue !== undefined) {
    const weight = getNumericWeight(
      resolvedMinValue,
      resolvedMidValue,
      clampedValue,
    );
    if (weight === null) {
      return null;
    }
    const isBetweenMinAndMid = weight <= 1;
    if (isBetweenMinAndMid) {
      rgb = lerpColor(minRgb, midRgb, weight);
    } else {
      const midToMaxWeight = getNumericWeight(
        resolvedMidValue,
        resolvedMaxValue,
        clampedValue,
      );
      if (midToMaxWeight === null) {
        return null;
      }
      rgb = lerpColor(midRgb, maxRgb, midToMaxWeight);
    }
  } else {
    const weight = getNumericWeight(
      resolvedMinValue,
      resolvedMaxValue,
      clampedValue,
    );
    if (weight === null) {
      return null;
    }
    rgb = lerpColor(minRgb, maxRgb, weight);
  }

  return rgbToHex(rgb);
}

// --- Where clause evaluation ---

type Ternary = boolean | null;
type ResolvedStyleRuleValue = string | number | bigint | boolean | null;

const UNRESOLVED = Symbol('unresolved');

function resolveNativeValue(
  value: unknown,
): ResolvedStyleRuleValue | typeof UNRESOLVED {
  if (value === null) {
    return null;
  }
  if (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint' ||
    typeof value === 'number'
  ) {
    return value;
  }
  if (value instanceof Date) {
    const ts = value.getTime();
    return isNaN(ts) ? null : ts;
  }
  if (isDatetimeLiteral(value)) {
    return parseDateString(value.datetime);
  }
  if (isLocalDatetimeLiteral(value)) {
    return parseLocalDateString(value.localdatetime);
  }
  if (isTimeLiteral(value)) {
    return parseTimeString(value.time);
  }
  if (isBigintLiteral(value)) {
    return resolveNumericLiteral(value);
  }
  return UNRESOLVED;
}

function resolveCellValue(
  cellVal: unknown,
  converter?: DataGridStyleRuleValueConverter,
): ResolvedStyleRuleValue {
  if (cellVal === undefined) {
    return null;
  }
  const native = resolveNativeValue(cellVal);
  if (native !== UNRESOLVED) {
    return native;
  }
  if (converter !== undefined) {
    const converted = converter(cellVal);
    if (converted === undefined) {
      return null;
    }
    const fromConverted = resolveNativeValue(converted);
    if (fromConverted !== UNRESOLVED) {
      return fromConverted;
    }
  }
  return null;
}

function resolveValue(
  value: DataGridStyleRuleOperand,
  rowData: Record<string, unknown>,
  converters?: DataGridStyleRuleValueConverters,
): ResolvedStyleRuleValue {
  if (typeof value === 'object' && value !== null && 'column' in value) {
    return resolveCellValue(rowData[value.column], converters?.[value.column]);
  }
  const resolved = resolveNativeValue(value);
  return resolved === UNRESOLVED ? null : resolved;
}

function safeCompare(
  left: DataGridStyleRuleOperand,
  right: DataGridStyleRuleOperand,
  rowData: Record<string, unknown>,
  compareFn: (
    leftVal: NonNullable<ResolvedStyleRuleValue>,
    rightVal: NonNullable<ResolvedStyleRuleValue>,
  ) => boolean,
  converters?: DataGridStyleRuleValueConverters,
): Ternary {
  const leftVal = resolveValue(left, rowData, converters);
  const rightVal = resolveValue(right, rowData, converters);
  if (leftVal === null || rightVal === null) {
    return null;
  }
  return compareFn(leftVal, rightVal);
}

function safeStringCompare(
  left: DataGridStyleRuleOperand,
  right: DataGridStyleRuleOperand,
  rowData: Record<string, unknown>,
  compareFn: (leftVal: string, rightVal: string) => boolean,
  converters?: DataGridStyleRuleValueConverters,
): Ternary {
  const leftVal = resolveValue(left, rowData, converters);
  const rightVal = resolveValue(right, rowData, converters);
  if (
    leftVal === null ||
    rightVal === null ||
    typeof leftVal !== 'string' ||
    typeof rightVal !== 'string'
  ) {
    return null;
  }
  return compareFn(leftVal, rightVal);
}

function evaluateWhere(
  rowData: Record<string, unknown>,
  where?: DataGridStyleRuleWhere,
  converters?: DataGridStyleRuleValueConverters,
): Ternary {
  if (!where) {
    return true;
  }

  if ('equal' in where) {
    const [left, right] = where.equal;
    const leftVal = resolveValue(left, rowData, converters);
    const rightVal = resolveValue(right, rowData, converters);
    if (leftVal === null || rightVal === null) {
      return null;
    }
    if (
      (typeof leftVal === 'number' || typeof leftVal === 'bigint') &&
      (typeof rightVal === 'number' || typeof rightVal === 'bigint')
    ) {
      return numericValuesEqual(leftVal, rightVal);
    }
    return leftVal === rightVal;
  }

  if ('not' in where) {
    const isMatch = evaluateWhere(rowData, where.not, converters);
    return isMatch === null ? null : !isMatch;
  }

  if ('lessThan' in where) {
    return safeCompare(
      where.lessThan[0],
      where.lessThan[1],
      rowData,
      (leftVal, rightVal) => leftVal < rightVal,
      converters,
    );
  }

  if ('lessThanOrEqual' in where) {
    return safeCompare(
      where.lessThanOrEqual[0],
      where.lessThanOrEqual[1],
      rowData,
      (leftVal, rightVal) => leftVal <= rightVal,
      converters,
    );
  }

  if ('greaterThan' in where) {
    return safeCompare(
      where.greaterThan[0],
      where.greaterThan[1],
      rowData,
      (leftVal, rightVal) => leftVal > rightVal,
      converters,
    );
  }

  if ('greaterThanOrEqual' in where) {
    return safeCompare(
      where.greaterThanOrEqual[0],
      where.greaterThanOrEqual[1],
      rowData,
      (leftVal, rightVal) => leftVal >= rightVal,
      converters,
    );
  }

  if ('contains' in where) {
    return safeStringCompare(
      where.contains[0],
      where.contains[1],
      rowData,
      (leftVal, rightVal) => leftVal.includes(rightVal),
      converters,
    );
  }

  if ('startsWith' in where) {
    return safeStringCompare(
      where.startsWith[0],
      where.startsWith[1],
      rowData,
      (leftVal, rightVal) => leftVal.startsWith(rightVal),
      converters,
    );
  }

  if ('endsWith' in where) {
    return safeStringCompare(
      where.endsWith[0],
      where.endsWith[1],
      rowData,
      (leftVal, rightVal) => leftVal.endsWith(rightVal),
      converters,
    );
  }

  if ('isNull' in where) {
    const value = resolveValue(where.isNull, rowData, converters);
    return value === null;
  }

  if ('and' in where) {
    let hasNull = false;
    for (const clause of where.and) {
      const isMatch = evaluateWhere(rowData, clause, converters);
      if (isMatch === false) {
        return false;
      }
      if (isMatch === null) {
        hasNull = true;
      }
    }
    return hasNull ? null : true;
  }

  if ('or' in where) {
    let hasNull = false;
    for (const clause of where.or) {
      const isMatch = evaluateWhere(rowData, clause, converters);
      if (isMatch === true) {
        return true;
      }
      if (isMatch === null) {
        hasNull = true;
      }
    }
    return hasNull ? null : false;
  }

  return false;
}

// --- Style resolution ---

function resolveAppliedStyle(
  apply: DataGridCellStyle,
  rowData: Record<string, unknown>,
  converters?: DataGridStyleRuleValueConverters,
): React.CSSProperties | undefined {
  const result: React.CSSProperties = {};
  let hasStyle = false;

  if (apply.color && !apply.color.isDisabled) {
    result.backgroundColor = apply.color.value;
    hasStyle = true;
  }

  if (apply.textColor && !apply.textColor.isDisabled) {
    result.color = apply.textColor.value;
    hasStyle = true;
  }

  if (apply.colorRange && !apply.colorRange.isDisabled) {
    const interpolated = getInterpolatedColor(
      apply.colorRange,
      rowData,
      converters,
    );
    if (interpolated !== null) {
      result.backgroundColor = interpolated;
      hasStyle = true;
    }
  }

  if (apply.textColorRange && !apply.textColorRange.isDisabled) {
    const interpolated = getInterpolatedColor(
      apply.textColorRange,
      rowData,
      converters,
    );
    if (interpolated !== null) {
      result.color = interpolated;
      hasStyle = true;
    }
  }

  return hasStyle ? result : undefined;
}

export type ComputedDataGridStyles = {
  cellStyles: Record<string, React.CSSProperties | undefined>;
};

type PriorityEntry = { value: string; priority: number };
type PropPriorityMap = Record<string, PriorityEntry>;

/**
 * Computes styles for all cells in a row based on the given style rules.
 * When a rule's `column` is `null`, its style is applied to every cell.
 *
 * Used internally by hooks, but exported for context-free usage.
 */
export function computeDataGridRowStyles(
  rules: DataGridStyleRule[],
  rowData: Record<string, unknown>,
  converters?: DataGridStyleRuleValueConverters,
): ComputedDataGridStyles {
  const enabledRules = rules.filter((rule) => !rule.isDisabled);
  const totalRules = enabledRules.length;

  const sortedRules = enabledRules
    .map((rule, index) => {
      if (rule.priority !== undefined && rule.priority < 0) {
        throw new Error(
          `DataGridStyleRule priority must be >= 0, got ${rule.priority}. Negative values are reserved for internal use.`,
        );
      }

      return {
        ...rule,
        _order: index,
        _priority: rule.priority ?? index - totalRules,
      };
    })
    .sort((leftRule, rightRule) => {
      if (leftRule._priority !== rightRule._priority) {
        return leftRule._priority - rightRule._priority;
      }
      return leftRule._order - rightRule._order;
    });

  const cellPriorityMap: Record<string, PropPriorityMap> = {};

  for (const rule of sortedRules) {
    const isMatching = evaluateWhere(rowData, rule.where, converters) === true;
    if (!isMatching) {
      continue;
    }

    const style = resolveAppliedStyle(rule.apply, rowData, converters);
    if (!style) {
      continue;
    }

    const effectivePriority = rule._priority;
    const affectedColumns =
      rule.column === null ? Object.keys(rowData) : [rule.column];

    for (const col of affectedColumns) {
      if (!cellPriorityMap[col]) {
        cellPriorityMap[col] = {};
      }
      for (const [prop, value] of Object.entries(style)) {
        const existing = cellPriorityMap[col][prop];
        if (!existing || effectivePriority >= existing.priority) {
          cellPriorityMap[col][prop] = {
            priority: effectivePriority,
            value: value as string,
          };
        }
      }
    }
  }

  const cellStyles: Record<string, React.CSSProperties | undefined> = {};
  for (const [col, propMap] of Object.entries(cellPriorityMap)) {
    const style: React.CSSProperties = {};
    let hasProp = false;
    for (const [prop, entry] of Object.entries(propMap)) {
      (style as Record<string, string>)[prop] = entry.value;
      hasProp = true;
    }

    // If no rule set an explicit text color for this cell, but a rule did set a
    // background: pick an accessible default so the user isn't left with
    // low-contrast text. An explicit `textColor`/`textColorRange` (even one
    // set to a color close to the background) always wins over this default.
    if (
      style.backgroundColor &&
      !style.color &&
      validHex(style.backgroundColor)
    ) {
      style.color = tinycolor
        .mostReadable(
          style.backgroundColor,
          [
            tokens.theme.light.color.neutral.text.default,
            tokens.theme.dark.color.neutral.text.default,
          ],
          // If none of the custom colors provided meet the required readability the function
          // falls back to pure black or pure white, whichever has better contrast
          { includeFallbackColors: true },
        )
        .toString();
      hasProp = true;
    }

    cellStyles[col] = hasProp ? style : undefined;
  }

  return { cellStyles };
}

/**
 * Evaluate style rules for a single cell.
 * Pure function -- no React context needed.
 *
 * @param rules - Array of style rules to evaluate
 * @param rowData - The row's data as a record (column id to value)
 * @param columnId - Returns the computed style for this specific cell.
 * @param converters - Optional per-column value converters for complex cell types.
 */
export function evaluateDataGridStyleRules(
  rules: DataGridStyleRule[],
  rowData: Record<string, unknown>,
  columnId: string,
  converters?: DataGridStyleRuleValueConverters,
): React.CSSProperties | undefined {
  const { cellStyles } = computeDataGridRowStyles(rules, rowData, converters);
  return cellStyles[columnId];
}
